import { fileExtension, filename } from '@configurations/file-export.configuration';
import { ExportGateway } from '@scenario/gateways/export.gateway';
import { Scenario, getScenarioKey } from '@scenario/models/scenario.model';
import { DateProvider } from '@scenario/providers/date.provider';
import { environment } from '../../../../../environments/environment';
import * as XLSX from 'xlsx';
import { UnitRate } from '@scenario/models/unit-rate.model';

export class XlsxExportGateway implements ExportGateway {
  private readonly filename: string = filename;
  private readonly fileExtension: string = fileExtension;
  private readonly appLink: string = environment.frontend_url;
  private readonly esparkLink: string = 'https://espark.totalenergies.com/';

  constructor(private readonly dateProvider: DateProvider) {}

  async exportAsXlsx(scenarios: Scenario[], rawScenarios: Scenario[], unitRates: UnitRate[]): Promise<Scenario[]> {
    this.export(scenarios, rawScenarios, unitRates);
    return scenarios;
  }

  private readonly export = (scenarios: Scenario[], rawScenarios: Scenario[], unitRates: UnitRate[]): void => {
    let forecastDateRange: YearRange = { min: null, max: null };
    let historicalDateRange: YearRange = { min: null, max: null };
    let sourceForecastDateRange: YearRange = { min: null, max: null };
    let sourceHistoricalDateRange: YearRange = { min: null, max: null };
    const { unitRows, unitDateRange } = this.formatUnit(unitRates);

    scenarios.forEach(scenario => {
      forecastDateRange = this.generateDateRange(scenario.pricePerYear, forecastDateRange);
      historicalDateRange = this.generateDateRange(scenario.historicalPricePerYear, historicalDateRange);
    });

    rawScenarios.forEach(scenario => {
      sourceForecastDateRange = this.generateDateRange(scenario.pricePerYear, forecastDateRange);
      sourceHistoricalDateRange = this.generateDateRange(scenario.historicalPricePerYear, historicalDateRange);
    });

    const forecastScenarios = scenarios.map(scenario => ({
      ...scenario,
      historicalPricePerYear: {},
    }));

    const historicalScenarios = scenarios.map(scenario => ({
      ...scenario,
      pricePerYear: {},
    }));

    const rawForecastScenarios = rawScenarios.map(scenario => ({
      ...scenario,
      historicalPricePerYear: {},
    }));

    const rawHistoricalScenarios = rawScenarios.map(scenario => ({
      ...scenario,
      pricePerYear: {},
    }));

    const forecastWorksheet = XLSX.utils.json_to_sheet(this.formatRows(forecastScenarios, true), {
      header: [
        'Scenario ID',
        'Country',
        'Region',
        'Source',
        'Release',
        'Publication Date',
        'Scenario',
        'Weather Year',
        'Baseload/Captured',
        'Currency',
        'Real Year / Nominal Terms',
        ...this.generateYearsToDisplay(forecastDateRange),
      ],
    });

    const historicalWorksheet = XLSX.utils.json_to_sheet(this.formatRows(historicalScenarios, true, true), {
      header: [
        'Scenario ID',
        'Country',
        'Region',
        'Source',
        'Release',
        'Publication Date',
        'Scenario',
        'Weather Year',
        'Baseload/Captured',
        'Currency',
        'Real Year / Nominal Terms',
        ...this.generateYearsToDisplay(historicalDateRange),
      ],
    });

    const rawForecastWorksheet = XLSX.utils.json_to_sheet(this.formatRows(rawForecastScenarios, false), {
      header: [
        'Scenario ID',
        'Country',
        'Region',
        'Source',
        'Release',
        'Publication Date',
        'Scenario',
        'Weather Year',
        'Baseload/Captured',
        'Currency',
        'Real Year / Nominal Terms',
        ...this.generateYearsToDisplay(sourceForecastDateRange),
      ],
    });

    const rawHistoricalWorksheet = XLSX.utils.json_to_sheet(this.formatRows(rawHistoricalScenarios, false, true), {
      header: [
        'Scenario ID',
        'Country',
        'Region',
        'Source',
        'Release',
        'Publication Date',
        'Scenario',
        'Weather Year',
        'Baseload/Captured',
        'Currency',
        'Real Year / Nominal Terms',
        ...this.generateYearsToDisplay(sourceHistoricalDateRange),
      ],
    });

    const worksheetUnitRates = XLSX.utils.json_to_sheet(unitRows, {
      header: [
        'From',
        'To',
        ...this.generateYearsToDisplay(unitDateRange),
      ],
    });

    this.createTemplate(forecastWorksheet, true);
    this.createTemplate(historicalWorksheet, false);
    this.createTemplate(rawForecastWorksheet, false);
    this.createTemplate(rawHistoricalWorksheet, false);
    this.createTemplate(worksheetUnitRates, false);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, forecastWorksheet, 'Price Scenarios');
    XLSX.utils.book_append_sheet(workbook, historicalWorksheet, 'Historical Prices');
    XLSX.utils.book_append_sheet(workbook, worksheetUnitRates, 'Conversion factors');
    XLSX.utils.book_append_sheet(workbook, rawForecastWorksheet, 'PS Source data');
    XLSX.utils.book_append_sheet(workbook, rawHistoricalWorksheet, 'HP Source data');

    XLSX.writeFile(
      workbook,
      `${this.filename}${this.generateUTCTimestamp()}${this.fileExtension}`,
      {
        compression: true,
      },
    );
  };

  private generateYearsToDisplay(dateRange: YearRange) {
    const yearsToDisplay: string[] = [];
    if (dateRange.min && dateRange.max) {
      for (let year = dateRange.min; year <= dateRange.max; year++) {
        yearsToDisplay.push(year.toString());
      }
    }
    return yearsToDisplay;
  }

  private readonly formatRows = (scenarios: Scenario[], dataConverted?: boolean, historicalData?: boolean) => {
    const rows: xlsxRow[] = scenarios.map(scenario => {
      return {
        'Scenario ID': getScenarioKey(scenario),
        Country: scenario.zoneName,
        Region: scenario.zoneName + ' - ' + scenario.zoneAlias,
        Source: scenario.source,
        'Release': scenario.sourcePublicationDate,
        'Publication Date': this.dateProvider.convertToLocaleString(
          scenario.unifiedPublicationDate,
        ),
        Scenario: scenario.basecase,
        'Weather Year': scenario.weatherYear,
        'Baseload/Captured': scenario.subtype,
        Currency: historicalData ? scenario.historicalUnitRateCurrency ?? scenario.unitRateCurrency : scenario.unitRateCurrency,
        'Real Year / Nominal Terms': historicalData
          ? scenario.historicalUnitRateTermYear ?? scenario.unitRateTermYear
          : scenario.unitRateTermYear,
        ...scenario.historicalPricePerYear,
        ...scenario.pricePerYear,
      };
    });

    rows.push({}, {}, {});
    if (dataConverted) {
    rows.push({'Scenario ID': 'All merchant prices in investment / M&A dossiers have to be validated by FPI, in case of doubt, contact the analyst in charge of the scenario.'});
    rows.push({'Scenario ID': 'All data are for internal use only.'});
    rows.push({});
    const lastRowPriceScenariosFinder = `Those data have been exported from Price Scenarios Finder (${
      this.appLink
    }) on the ${this.dateProvider.currentDateAsLocaleString()}.`;
    const lastRowEspark = `Those data are based on the eSpark Data Product called Scenarios Finder, you can visit eSpark Data platform here (${this.esparkLink}).`;
    rows.push({ 'Scenario ID': lastRowPriceScenariosFinder });
    rows.push({ 'Scenario ID': lastRowEspark });
    }
    return rows ;
  };

  private generateDateRange(data: Record<number, number | null>, dateRange: YearRange) {
    const keysAsNumber = Object.keys(data).map(k => Number(k));

    if (keysAsNumber.length === 0) {
      return dateRange;
    }

    const min = Math.min(...keysAsNumber);
    const max = Math.max(...keysAsNumber);

    if (!dateRange.min || min < dateRange.min) {
      dateRange.min = min;
    }
    if (!dateRange.max || max > dateRange.max) {
      dateRange.max = max;
    }
    return dateRange;
  }

  generateUTCTimestamp = (): string => {
    const now = new Date();

    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0'); // Les mois commencent à 0
    const day = String(now.getUTCDate()).padStart(2, '0');
    const hours = String(now.getUTCHours()).padStart(2, '0');
    const minutes = String(now.getUTCMinutes()).padStart(2, '0');

    return `${year}${month}${day}-${hours}:${minutes}`;
  };

  private formatTerm(term: string): string {
    if (term === 'EXCHANGE_RATE_ONLY') {
      return 'EXCHANGE RATE';
    } else if (term === 'INFLATION_RATE_ONLY') {
      return 'INFLATION RATE';
    } else {
      return term;
    }
  }

  private formatUnit(unitRates: UnitRate[]) {
    let unitDateRange: YearRange = { min: null, max: null };
    const termGroupOrder = (term: string): number => {
      if (term === 'EXCHANGE_RATE_ONLY') {
        return 1;
      }
      if (term === 'INFLATION_RATE_ONLY') {
        return 2;
      }
      return 0;
    };

    const unitRows: unitConv[] = unitRates.filter(unitRate => unitRate.from.term === 'INFLATION_RATE_ONLY' || !((unitRate.from.currency === unitRate.to.currency)
      && (unitRate.from.term === unitRate.to.term) && (unitRate.from.realYear === unitRate.to.realYear)))
      .sort((a, b) => {
        const groupDiff = termGroupOrder(a.from.term) - termGroupOrder(b.from.term);
        if (groupDiff !== 0) {
          return groupDiff;
        }
        const currencyOrder = (currency: string): number => (currency === 'EUR' ? 0 : 1);
        const eurDiff = currencyOrder(a.to.currency) - currencyOrder(b.to.currency);
        if (eurDiff !== 0) {
          return eurDiff;
        }
        const currencyDiff = a.to.currency.localeCompare(b.to.currency);
        if (currencyDiff !== 0) {
          return currencyDiff;
        }
        if (termGroupOrder(a.from.term) === 0) {
          return (a.to.realYear ?? 0) - (b.to.realYear ?? 0);
        }
        return 0;
      })
      .map(unitRate => {
      unitDateRange = this.generateDateRange(unitRate.ratesByYear, unitDateRange);
      return {
        'From': unitRate.from.currency + '(' + this.formatTerm(unitRate.from.term) + ' ' + (unitRate.from.realYear ?? '') + ')',
        'To': unitRate.to.currency + '(' + this.formatTerm(unitRate.to.term) + ' ' + (unitRate.to.realYear ?? '')  + ')',
        ...unitRate.ratesByYear,
      };
    });
    unitRows.push({});
    unitRows.push({}, {}, {});
    return { unitRows, unitDateRange };
  }

  private createTemplate(worksheet: XLSX.WorkSheet, firstPage: boolean) {
    const range = XLSX.utils.decode_range(worksheet['!ref'] ?? '');
    const totalColumns = range.e.c;
    const totalRows = range.e.r + 1;

    // Apply format to every cell
    for (let C = range.s.c; C <= range.e.c; ++C) {
      for (let R = range.s.r + 1; R <= range.e.r; ++R) {
        // Commence à R + 1 pour éviter l'en-tête
        const cellAddress = { c: C, r: R };
        const cellRef = XLSX.utils.encode_cell(cellAddress);
        const cell = worksheet[cellRef];

        // Check if cell is a "number" type
        if (cell && cell.t === 'n') {
          cell.z = '0.0';
        }
      }
    }

    worksheet['!merges'] = worksheet['!merges'] || [];
    const mergeRange = {
      s: { r: totalRows - 2, c: 0 }, // Début: première cellule de la dernière ligne
      e: { r: totalRows - 2, c: totalColumns }, // Fin: dernière cellule de la dernière ligne
    };
    worksheet['!merges'].push(mergeRange);
    const mergeRange2 = {
      s: { r: totalRows - 1, c: 0 }, // Début: première cellule de la dernière ligne
      e: { r: totalRows - 1, c: totalColumns }, // Fin: dernière cellule de la dernière ligne
    };
    worksheet['!merges'].push(mergeRange2);

    // Ajuster la largeur des colonnes pour une meilleure lisibilité
    worksheet['!cols'] = [];
    for (let C = range.s.c; C <= range.e.c; ++C) {
      worksheet['!cols'][C] = { wch: 12 }; // Largeur de 12 caractères pour chaque colonne
    }

    if (firstPage) {

      worksheet[`A${totalRows - 1}`].l = {
        Target: this.appLink,
        Tooltip: 'Go to Price Scenarios Finder',
      };

      worksheet[`A${totalRows}`].l = {
        Target: this.esparkLink,
        Tooltip: 'Go to eSpark Data platform',
      };
    }

  }
}

type YearRange = {
  min: number | null;
  max: number | null;
};

type xlsxRow = {
  'Scenario ID'?: string;
  Country?: string;
  Region?: string;
  Source?: string;
  'Release'?: string;
  'Publication Date'?: string;
  Scenario?: string;
  'Weather Year'?: string;
  'Baseload/Captured'?: string;
};

type unitConv = {
  'From'?: string;
  'To'?: string;
};
